// Budget Simulator: runs one plan per point of a grid of total budgets over the same period and
// reads them back as a Total Saturation Curve. Free points are Plan Optimizers (the optimizer picks
// the mix at each budget); Frozen points are Plan Calculators whose media plan spreads each budget
// with a fixed mix, each channel over time following its own historical pattern.
// See docs/budget-simulator.md.

import moment from 'moment'
import { weeklyPeriodDates } from '@/utils/weeklyPeriod.js'
import { effectiveModelStart, clampMonthlyStart, clampWeeklyStart } from '@/utils/modelStart.js'
import { isKpiHidden, hasHiddenKpis, firstVisibleOutputName } from '@/utils/hiddenKpis.js'

export const MAX_GRID_POINTS = 10
export const DEFAULT_GRID = { minPct: 20, maxPct: 180, points: 5 }

// The budgets of the grid, as percentages of the reference budget spread evenly from minPct to maxPct.
export function buildGrid (referenceBudget, minPct, maxPct, points) {
  const count = Math.max(1, Math.min(MAX_GRID_POINTS, Math.round(points)))
  const grid = []
  for (let i = 0; i < count; i++) {
    const pct = count === 1 ? minPct : minPct + (maxPct - minPct) * i / (count - 1)
    const rounded = Math.round(pct * 10) / 10
    const budget = Math.round(referenceBudget * rounded / 100)
    if (rounded > 0 && budget > 0 && !grid.some(point => point.budget === budget)) {
      grid.push({ pct: rounded, budget: budget })
    }
  }
  return grid
}

// The model's dates ({ start, end, all }): those of its brand when the config has brands, as the
// Plan Calculator reads them.
export function modelDates (client, config) {
  if (config && config.brands) {
    const brand = (client.type === 'mmm' || !client.custom_model) ? Object.keys(config.brands)[0] : client.custom_model.brand
    if (config.brands[brand] && config.brands[brand].dates) {
      return config.brands[brand].dates
    }
  }
  return (config && config.dates) || {}
}

// The "Last Year" window back from the last data, the one the Plan Calculator's "Last Year"
// templates use: the last 12 months, or the last 52 Sunday-aligned weeks on weekly models.
export function lastYearWindow (report, frequency, clientDateCreated) {
  const v2 = (report && report.v2) || {}
  const weekly = frequency === 'week' && v2.media_spend_weekly && Object.keys(v2.media_spend_weekly).length > 0
  const source = weekly ? v2.media_spend_weekly : v2.media_spend
  if (!source || Object.keys(source).length === 0) return null
  const dates = source[Object.keys(source)[0]].x
  if (!dates || dates.length === 0) return null
  const start = effectiveModelStart(dates[0], frequency, clientDateCreated)
  if (weekly) {
    const dateTo = moment(dates[dates.length - 1])
    if (dateTo.day() !== 0) {
      dateTo.subtract(dateTo.day(), 'days')
    }
    return {
      date_from: clampWeeklyStart(dateTo.clone().subtract(51, 'weeks').format('YYYY-MM-DD'), start),
      date_to: dateTo.format('YYYY-MM-DD')
    }
  }
  const dateTo = dates[dates.length - 1]
  return {
    date_from: clampMonthlyStart(moment(dateTo).subtract(11, 'months').startOf('month').format('YYYY-MM-DD'), start),
    date_to: dateTo
  }
}

// The "Next Year" window and its budget, from the model report's budget propagation.
export function nextYearWindow (report, frequency) {
  const propagation = report && report.v2 && report.v2.budget_propagation
  if (!propagation || !propagation.dates) return null
  const available = granularity => propagation[granularity] && Object.keys(propagation[granularity]).length > 0 &&
    Array.isArray(propagation.dates[granularity]) && propagation.dates[granularity].length > 0
  let granularity = null
  if (frequency === 'week' && available('weekly')) {
    granularity = 'weekly'
  } else if (available('monthly')) {
    granularity = 'monthly'
  } else if (available('weekly')) {
    granularity = 'weekly'
  }
  if (!granularity) return null
  const dates = propagation.dates[granularity]
  let budget = 0
  Object.keys(propagation[granularity]).forEach(channel => {
    (propagation[granularity][channel] || []).forEach(value => { budget += Math.round(Number(value) || 0) })
  })
  return { date_from: dates[0], date_to: dates[dates.length - 1], budget: budget }
}

// The periods a plan over [dateFrom, dateTo] has, the same columns the Plan Calculator shows:
// the model's monthly dates touching the window, or its Sundays for weekly models.
export function planPeriods (dateFrom, dateTo, frequency, monthlyDates, modelEnd) {
  if (!dateFrom || !dateTo) return []
  if (frequency === 'week') {
    return weeklyPeriodDates(dateFrom, dateTo, modelEnd)
  }
  const fromSliced = dateFrom.slice(0, 7)
  const toSliced = dateTo.slice(0, 7)
  return (monthlyDates || []).filter(date => date.slice(0, 7) >= fromSliced && date.slice(0, 7) <= toSliced).sort()
}

// Per channel, a lookup from a period key (YYYY-MM for months, the week's date for weeks) to spend.
function spendLookup (spendSource, frequency) {
  const lookup = {}
  Object.keys(spendSource || {}).forEach(channel => {
    const series = spendSource[channel]
    const byKey = {}
    if (series && Array.isArray(series.x)) {
      series.x.forEach((date, i) => {
        const key = frequency === 'week' ? date : date.slice(0, 7)
        byKey[key] = (byKey[key] || 0) + (Number(series.y[i]) || 0)
      })
    }
    lookup[channel] = byKey
  })
  return lookup
}

// The historical spend each channel had in each period of the plan. A period past the last data is
// read from the same period a year earlier (as many years back as it takes), so a future window
// gets the seasonality of the last year of data. Weekly models without weekly spend read a share of
// the month the week is in.
export function historicalSpend (report, channels, periods, frequency) {
  const v2 = (report && report.v2) || {}
  const weekly = frequency === 'week' && v2.media_spend_weekly && Object.keys(v2.media_spend_weekly).length > 0
  const source = weekly ? v2.media_spend_weekly : v2.media_spend
  const keyFrequency = weekly ? 'week' : 'month'
  const lookup = spendLookup(source, keyFrequency)
  const anyChannel = Object.keys(lookup).find(channel => Object.keys(lookup[channel]).length > 0)
  const keys = anyChannel ? Object.keys(lookup[anyChannel]).sort() : []
  const firstKey = keys[0]
  const lastKey = keys[keys.length - 1]
  const toKey = date => keyFrequency === 'week' ? date : date.slice(0, 7)
  const shiftBack = date => keyFrequency === 'week'
    ? moment(date).subtract(52, 'weeks').format('YYYY-MM-DD')
    : moment(date).subtract(12, 'months').format('YYYY-MM-DD')

  const result = {}
  channels.forEach(channel => {
    result[channel] = periods.map(period => {
      if (!lastKey) return 0
      let date = period
      while (toKey(date) > lastKey) {
        date = shiftBack(date)
      }
      if (toKey(date) < firstKey) return 0
      const channelLookup = lookup[channel] || {}
      const value = channelLookup[toKey(date)] || 0
      // A weekly plan on monthly spend: the week takes its share of its month.
      return (frequency === 'week' && !weekly) ? value * 7 / moment(date).daysInMonth() : value
    })
  })
  return result
}

// The historical total and each channel's share of it (in %), over the plan's periods.
export function historicalMix (spend) {
  const totals = {}
  let total = 0
  Object.keys(spend).forEach(channel => {
    totals[channel] = spend[channel].reduce((acc, value) => acc + value, 0)
    total += totals[channel]
  })
  const mix = {}
  Object.keys(totals).forEach(channel => {
    mix[channel] = total > 0 ? Math.round(10000 * totals[channel] / total) / 100 : 0
  })
  return { total: Math.round(total), mix: mix }
}

// Shares of 100 that add up to exactly 100 with two decimals, the rounding error going to the largest.
export function normalizeMix (mix) {
  const channels = Object.keys(mix)
  const total = channels.reduce((acc, channel) => acc + (Number(mix[channel]) || 0), 0)
  const result = {}
  if (total <= 0) return result
  channels.forEach(channel => {
    result[channel] = Math.round(10000 * (Number(mix[channel]) || 0) / total) / 100
  })
  const sum = channels.reduce((acc, channel) => acc + result[channel], 0)
  const largest = channels.reduce((a, b) => result[a] >= result[b] ? a : b)
  result[largest] = Math.round((result[largest] + 100 - sum) * 100) / 100
  return result
}

// The media plan of a Frozen point: each channel gets its share of the budget, spread over the
// periods following its historical pattern (evenly when it had no spend). Values are whole units
// adding up to exactly the budget, as the Plan Calculator requires.
export function frozenMediaPlan (budget, mix, spend, periodCount) {
  const plan = {}
  const cells = []
  Object.keys(spend).forEach(channel => {
    const share = (Number(mix[channel]) || 0) / 100
    const pattern = spend[channel]
    const patternTotal = pattern.reduce((acc, value) => acc + value, 0)
    plan[channel] = []
    for (let t = 0; t < periodCount; t++) {
      const weight = patternTotal > 0 ? pattern[t] / patternTotal : 1 / periodCount
      const exact = budget * share * weight
      const value = Math.floor(exact)
      plan[channel].push(value)
      cells.push({ channel: channel, t: t, remainder: exact - value })
    }
  })
  // Hand the units lost to rounding down to the cells that lost the most.
  let missing = budget - Object.keys(plan).reduce((acc, channel) => acc + plan[channel].reduce((a, b) => a + b, 0), 0)
  cells.sort((a, b) => b.remainder - a.remainder)
  for (let i = 0; missing > 0 && cells.length > 0; i = (i + 1) % cells.length) {
    plan[cells[i].channel][cells[i].t] += 1
    missing--
  }
  return plan
}

// The plan docs of every grid point. They are ordinary plans, and the shapes match what the Plan
// Optimizer (no type, as the Strategy Check builds it) and the Plan Calculator write.
export function buildPoints (settings) {
  return settings.grid.map(point => {
    const base = {
      name: `${settings.name} · ${point.pct}%`,
      date_from: settings.date_from,
      date_to: settings.date_to,
      budget: point.budget,
      external_series_changes: {},
      budget_simulation_pct: point.pct
    }
    if (settings.mix_mode === 'frozen') {
      return {
        ...base,
        media_plan: frozenMediaPlan(point.budget, settings.mix, settings.spend, settings.periods.length),
        type: 'plan_simulation',
        media_plan_frequency: settings.media_plan_frequency,
        external_series_frequency: settings.media_plan_frequency
      }
    }
    return {
      ...base,
      restrictions: {},
      touchpoints: [],
      segments: [],
      optimizer_version: 1
    }
  })
}

export function parsedResults (plan) {
  if (!plan || !plan.results) return null
  if (typeof plan.results === 'string') {
    try {
      return JSON.parse(plan.results)
    } catch (e) {
      return null
    }
  }
  return plan.results
}

// The KPIs a point's results can be read for: every visible output, plus the Total when the
// outputs can be added up and none is hidden (the same rule as the plan details page).
export function outputNames (results, client, config) {
  if (!results || !results.sales || !results.sales.outputs) return []
  const names = Object.keys(results.sales.outputs).filter(name => !isKpiHidden(client, name))
  const metadata = config && config.metadata
  const combinable = !metadata || metadata.combinable_outputs !== false
  const configOutputs = config && config.outputs
  const additive = Object.keys(results.sales.outputs).every(name => {
    return !configOutputs || !configOutputs[name] || configOutputs[name].is_additive !== false
  })
  if ('total' in results.sales && combinable && additive && !hasHiddenKpis(client)) {
    names.push('Total')
  }
  return names
}

function salesFor (results, kpi, client) {
  const sales = results.sales || {}
  if (kpi && kpi !== 'Total' && sales.outputs && sales.outputs[kpi]) {
    return sales.outputs[kpi]
  }
  if ('total' in sales && !(sales.outputs && hasHiddenKpis(client))) {
    return sales
  }
  const first = firstVisibleOutputName(client, sales.outputs)
  return first ? sales.outputs[first] : {}
}

// One row per finished point, sorted by budget: the marketing-attributed sales of the KPI (the sum
// of every channel's attributed sales, as the plan's Media Sales chart adds them up), total ROAS,
// each channel's share of the spend and the plan's ROAS by channel.
export function aggregatePoints (points, kpi, client) {
  const rows = []
  points.forEach(point => {
    const results = parsedResults(point)
    if (!results || !results.sales) return
    const sales = salesFor(results, kpi, client)
    const marketing = sales.marketing || {}
    const marketingSales = Object.keys(marketing).reduce((acc, channel) => acc + (Number(marketing[channel].adstock) || 0), 0)
    const investMix = (results.invest && results.invest.mix) || {}
    const invested = Object.keys(investMix).reduce((acc, channel) => acc + (Number(investMix[channel].invest) || 0), 0)
    const budget = invested > 0 ? invested : point.budget
    const mix = {}
    Object.keys(investMix).forEach(channel => {
      mix[channel] = invested > 0 ? 100 * (Number(investMix[channel].invest) || 0) / invested : 0
    })
    rows.push({
      id: point.id,
      pct: point.budget_simulation_pct,
      budget: budget,
      marketingSales: marketingSales,
      roas: budget > 0 ? marketingSales / budget : 0,
      mix: mix,
      roasByChannel: results.roas_by_channel || null
    })
  })
  return rows.sort((a, b) => a.budget - b.budget)
}

function crossing (curve, level) {
  for (let i = 1; i < curve.length; i++) {
    if (curve[i].y >= level) {
      const a = curve[i - 1]
      const b = curve[i]
      return b.y === a.y ? b.x : a.x + (b.x - a.x) * (level - a.y) / (b.y - a.y)
    }
  }
  return curve[curve.length - 1].x
}

// The four boundaries of the optimal zones of a total curve, by the rule ngtorch uses for each
// channel's curve (NgReport.saturation_regions): where it reaches 10% of its maximum, where it
// grows fastest, where its ROAS (y / x) peaks and where it reaches 90% of its maximum. The maximum
// is the one inside the grid, so the zones depend on how far the grid reaches.
export function totalRegions (rows) {
  const curve = [{ x: 0, y: 0 }].concat(rows.map(row => ({ x: row.budget, y: row.marketingSales })))
  if (curve.length < 3) return null
  const maxY = Math.max.apply(null, curve.map(point => point.y))
  if (!(maxY > 0)) return null
  let steepest = 0
  let maxMarginal = curve[1].x / 2
  for (let i = 1; i < curve.length; i++) {
    const slope = (curve[i].y - curve[i - 1].y) / (curve[i].x - curve[i - 1].x)
    if (slope > steepest) {
      steepest = slope
      maxMarginal = (curve[i].x + curve[i - 1].x) / 2
    }
  }
  let bestRoas = -Infinity
  let maxRoas = curve[1].x
  curve.slice(1).forEach(point => {
    if (point.y / point.x > bestRoas) {
      bestRoas = point.y / point.x
      maxRoas = point.x
    }
  })
  return [crossing(curve, 0.1 * maxY), maxMarginal, maxRoas, crossing(curve, 0.9 * maxY)]
}

// The [from, to] of each optimal zone (Zones 2 to 5) of a total curve whose x axis ends at maxX,
// or null without four boundaries. The zones are always given: a boundary out of order (a grid
// that starts past the curve's knee, or a convex start) is pushed up to the previous one, so its
// zone collapses to from === to. Shared by the Total Saturation Curve and its XLSX download.
export function zoneBounds (regions, maxX) {
  if (!regions || regions.length !== 4) return null
  const bounds = []
  regions.concat([maxX]).forEach((bound, i) => {
    bounds.push(i === 0 ? bound : Math.max(bound, bounds[i - 1]))
  })
  return [0, 1, 2, 3].map(i => [bounds[i], bounds[i + 1]])
}

// Totals by date of a {channel: {x, y}} series.
function totalsByDate (series) {
  const totals = {}
  Object.keys(series || {}).forEach(channel => {
    const data = series[channel]
    if (!data || !Array.isArray(data.x)) return
    data.x.forEach((date, i) => {
      totals[date] = (totals[date] || 0) + (Number(data.y[i]) || 0)
    })
  })
  return totals
}

// Historical points for the total curve: one per period of the simulation (a week or a month, as
// the model goes) that has real data, with its spend and the sales the model attributes to media in
// it. The curve's x axis is the budget of the whole simulation, so each point is annualized to it:
// x and y are the period's figures times the simulation's number of periods, where the point would
// sit if every period were like this one. Periods past the last data are left out.
export function historicalPoints (report, kpi, frequency, periods) {
  if (!report || !report.v2 || !periods || periods.length === 0) return []
  const weekly = frequency === 'week'
  const suffix = weekly ? '_weekly' : ''
  let v2Sales = report.v2
  if (kpi && kpi !== 'Total' && report.outputs && report.outputs[kpi] && report.outputs[kpi].v2) {
    v2Sales = report.outputs[kpi].v2
  }
  // Weeks are keyed by their date, months by YYYY-MM (the report and the plan may date a month
  // differently).
  const toKey = date => weekly ? date : date.slice(0, 7)
  const byKey = totals => {
    const keyed = {}
    Object.keys(totals).forEach(date => {
      keyed[toKey(date)] = (keyed[toKey(date)] || 0) + totals[date]
    })
    return keyed
  }
  const spend = byKey(totalsByDate(report.v2['media_spend' + suffix]))
  const sales = byKey(totalsByDate(v2Sales['media_attributed_sales_decomposition_by_period' + suffix]))
  const scale = periods.length
  return periods
    .filter(period => toKey(period) in spend && toKey(period) in sales)
    .map(period => ({
      x: spend[toKey(period)] * scale,
      y: sales[toKey(period)] * scale,
      date: period,
      spend: spend[toKey(period)],
      sales: sales[toKey(period)],
      scale: scale
    }))
}
